<script setup lang="ts">
import { computed } from 'vue'
import { formatToInternational } from '@shared/utils'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import { formatVariance, type SellQuote, type TradeFeeLine } from '../../trading'
import SellSummaryCard, { type SellSummaryRow } from './SellSummaryCard.vue'
import {
  KEY_RISKS,
  GROSS_SALE_VALUE_SHEET,
  SELL_QUOTE_PENDING,
  buildSellFeeSheet,
  buildSellFeeVatSheet,
} from '../constants/sellCopy'
import { formatAmount } from '../utils/sellListingUtils'

// Sell 7 — Review & Terms (Figma 34796:138796): the full listing summary with
// each fee and its VAT exactly as the server priced them (sell-quote), the
// estimated earnings, then the key risks. The terms tick and "Create Listing"
// live in the drawer's sticky footer.

interface Props {
  propertyTitle: string
  tokens: number
  price: number
  /** Weighted-average cost per token — the performance-fee base. */
  averageCost: number
  variancePct: number | null
  /** Server quote for exactly these tokens + price; null while it prices. */
  quote: SellQuote | null
}

const props = defineProps<Props>()

/** "− AED 190.00" — a deduction row on the listing summary. */
const formatDeduction = (amount: number): string => `− ${formatAmount(amount)}`

const feeRows = (fee: TradeFeeLine): SellSummaryRow[] => {
  const rows: SellSummaryRow[] = [
    {
      label: fee.rate ? `${fee.label} (${fee.rate})` : fee.label,
      value: formatDeduction(fee.amount),
      info: buildSellFeeSheet(fee, props.averageCost),
    },
  ]
  if (fee.vatAmount > 0) {
    rows.push({
      label: `VAT on ${fee.label}`,
      value: formatDeduction(fee.vatAmount),
      info: buildSellFeeVatSheet(fee),
    })
  }
  return rows
}

const rows = computed<SellSummaryRow[]>(() => {
  const quote = props.quote
  const head: SellSummaryRow[] = [
    { label: 'Property', value: props.propertyTitle },
    { label: 'Tokens to list', value: formatToInternational(props.tokens) },
    { label: 'Price per token', value: formatAmount(props.price) },
    {
      label: 'Variance vs valuation',
      value: formatVariance(props.variancePct),
      tone: (props.variancePct ?? 0) < 0 ? 'success' : 'default',
    },
  ]
  if (!quote)
    return [...head, { label: 'Estimated Earnings', value: SELL_QUOTE_PENDING, isTotal: true }]
  return [
    ...head,
    {
      label: 'Gross sale value',
      value: formatAmount(quote.grossAmount),
      info: GROSS_SALE_VALUE_SHEET,
    },
    ...quote.fees.flatMap(feeRows),
    { label: 'Estimated Earnings', value: formatAmount(quote.netProceeds), isTotal: true },
  ]
})
</script>

<template>
  <Box direction="col" gap="14px">
    <SellSummaryCard eyebrow="LISTING SUMMARY" label-size="sm" :rows="rows" />

    <Box direction="col" gap="3" class="sell-review__risks">
      <Text
        is="h3"
        type="caption"
        weight="bold"
        color="var(--p-sliceal-gray-400)"
        class="sell-review__eyebrow"
        >KEY RISKS</Text
      >
      <Box is="ul" direction="col" gap="3" class="sell-review__list">
        <Box
          v-for="risk in KEY_RISKS"
          :key="risk"
          is="li"
          align="center"
          gap="10px"
          class="sell-review__risk"
        >
          <Box class="sell-review__bullet" aria-hidden="true" />
          <Text
            is="span"
            type="caption"
            color="var(--p-text-muted-color)"
            class="sell-review__risk-text"
            >{{ risk }}</Text
          >
        </Box>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.sell-review__risks {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid var(--p-sliceal-gray-200);
  border-radius: 16px;
  background: var(--p-surface-0);
  box-sizing: border-box;
}

.sell-review__eyebrow {
  margin: 0;
  line-height: 1.4;
}

.sell-review__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.sell-review__risk {
  padding: 3px 0;
}

.sell-review__bullet {
  flex-shrink: 0;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--p-surface-500);
}

.sell-review__risk-text {
  flex: 1;
  min-width: 0;
  line-height: 1.5;
}
</style>
